import { CoherenceCheck } from '@coherence-design-system/controls';
import {
  buildColumns,
  CheckboxVisibility,
  ColumnActionsMode,
  CommandBar,
  ConstrainMode,
  DetailsListLayoutMode,
  DetailsRow,
  Dialog,
  DialogType,
  Dropdown,
  FontIcon,
  IColumn,
  ICommandBarItemProps,
  IDetailsListCheckboxProps,
  IDetailsRowProps,
  IDropdownOption,
  Label,
  MarqueeSelection,
  mergeStyleSets,
  PivotItem,
  ProgressIndicator,
  Selection,
  SelectionMode,
  ShimmeredDetailsList,
  Stack,
  Text,
  TextField,
  TooltipHost,
  DefaultButton,
  DialogFooter,
  MessageBar,
  MessageBarType,
} from '@fluentui/react';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import * as React from 'react';
import { Context as ReactContext, useContext } from 'react';
import { useParams } from 'react-router-dom';

import * as PopupText from '../../../Common/CommonModels/PopupText';
import { SubscriberRequestRecord } from '../../../Common/ConsumerModels/SubscriberRequestRecord';
import { SubscriberRecord } from '../../../Common/ConsumerModels/SubscriberRecord';
import * as Styled from '../../../Common/Styles/portalManagement.styled';
import { useGetPermissions } from '../../../Hooks/Common/useGetPermissions';
import { useGetAllRequestsBySubscriber } from '../../../Hooks/Consumer/useGetAllRequestsBySubscriber';
import { AddSubscriberRequestPanel } from './AddSubscriberRequest/AddSubscriberRequestPanel';
import { SubscriberDetailsHeader, SubscriberDetailsHeaderLoading, SubscriberDetailsHeaderError } from './SubscriberManagementHeader';
import { ViewApprovedAttributesPanel } from './ViewApprovedAttributesPanel/ViewApprovedAttributesPanel';
import { ViewSubscriberRequestPanel } from './ViewSubscriberRequestPanel/ViewSubscriberRequestPanel';
import { sortItems } from '../../../Common/Components/SortItems';
import { commandBarRefreshItem } from '../../../Common/Components/RefreshButton';
import { MAX_NOTES_LENGTH, TRUNCATED_NOTES, renewalInProgress, renewalSuccess, renewalSuccessDescription, renewalError, renewalErrorDescription, PENDING_STATUSES } from '../../../Common/CommonModels/Constants';
import { useCreateRenewalRequest } from '../../../Hooks/Consumer/useCreateRenewalRequest';

// Helper function to convert egress options to display string
const getEgressOptionsDisplayString = (egressOptions: number[]): string => {
  const egressOptionStringList: string[] = [];
  egressOptions.forEach((egressOption) => {
    if (egressOption === 1) {
      egressOptionStringList.push('API');
    } else if (egressOption === 2) {
      egressOptionStringList.push('Service Bus');
    } else if (egressOption === 3) {
      egressOptionStringList.push('DataLake');
    } else if (egressOption === 4) {
      egressOptionStringList.push('Kusto');
    } else if (egressOption === 5) {
      egressOptionStringList.push('GraphQL Application');
    } else if (egressOption === 6) {
      egressOptionStringList.push('GraphQL Delegated');
    } else if (egressOption === 7) {
      egressOptionStringList.push('Snapshot');
    }
  });
  return egressOptionStringList.join(', ');
};

type SubscriberManagementProps = {
  addNew: boolean;
  subscriberRecord: SubscriberRecord;
};

export function SubscriberManagement(props: SubscriberManagementProps): React.ReactElement {
  const { addNew, subscriberRecord } = props;
  const { paramConsumerId, paramRequestId }: never = useParams();
  const { httpClient } = useContext(Context as ReactContext<IEmployeeExperienceContext>);
  const isSubscriberRecordLoading = false;
  const subscriberError: string | null = null;

  // Add Subscriber Request State
  const [renderRequestTable, setRenderRequestTable] = React.useState<boolean>(true);

  // Add Subscriber Request State
  const [addSubscriberRequestPanelOpen, setAddSubscriberRequestPanelOpen] = React.useState<boolean>(false);

  // Draft Subscriber Request State
  const [selectedEditedRequest, setSelectedEditedRequest] = React.useState<SubscriberRequestRecord>(null);
  const [editedSubscriberRequestPanelOpen, setEditedSubscriberRequestPanelOpen] = React.useState<boolean>(false);

  // View Subscriber Request State
  const [selectedViewRequest, setSelectedViewRequest] = React.useState<SubscriberRequestRecord>(null);
  const [viewSubscriberRequestPanelOpen, setViewSubscriberRequestPanelOpen] = React.useState<boolean>(false);

  // Remove Subscriber Request State
  const [removeRequestButtonDisabled, setRemoveRequestButtonDisabled] = React.useState<boolean>(true);

  // Attribute Subscriptions State
  const [viewAllAttributeSubscriptionPanelOpen, setViewAllAttributeSubscriptionPanelOpen] =
    React.useState<boolean>(false);

  // Popup from submitting Subscriber Request State
  const [showConfirmationPopup, setShowConfirmationPopup] = React.useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = React.useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = React.useState<boolean>(false);

  // Subscriber Requests Table State
  const [selectedPivotKey, setSelectedPivotKey] = React.useState('Requests');
  const [selectedGridData, setSelectedGridData] = React.useState<SubscriberRequestRecord>(null);
  const [isRenewEnabled, setIsRenewEnabled] = React.useState<boolean>(false);
  const [showRenewalPopup, setShowRenewalPopup] = React.useState<boolean>(false);
  const [showRenewalSuccess, setShowRenewalSuccess] = React.useState<boolean>(false);
  const [showRenewalError, setShowRenewalError] = React.useState<boolean>(false);
  const [showPendingBlockDialog, setShowPendingBlockDialog] = React.useState<boolean>(false);
  const [renewalReloadTriggered, setRenewalReloadTriggered] = React.useState<boolean>(false);

  const [createRenewalRequest, isRenewalLoading] = useCreateRenewalRequest();
  const shouldReload = (showSuccessMessage && addSubscriberRequestPanelOpen == false) || 
                       (showRenewalSuccess && !showRenewalPopup);  

  // Hooks
  const [myPermissions, isMyPermissionsLoading] = useGetPermissions();
  const [subscriberRequestsList, isSubscriberRequestsListLoading] = useGetAllRequestsBySubscriber(
    paramConsumerId,
    shouldReload
  );

  // Subscriber Requests Table Data
  const [sortedSubscriberRequestsList, setSortedSubscriberRequestsList] = React.useState<SubscriberRequestRecord[]>(subscriberRequestsList ?? []);
  const [filterByColumn, setFilterByColumn] = React.useState<string>('');
  const [filterByValue, setFilterByValue] = React.useState<string>('');
  // Update state after data load
  React.useEffect(() => {
    if(!isSubscriberRequestsListLoading){
      setSortedSubscriberRequestsList(subscriberRequestsList);
    }

     if (renewalReloadTriggered) {
      setShowRenewalSuccess(false);
      setRenewalReloadTriggered(false);
    }

    if (!isSubscriberRecordLoading && addNew) {
      setAddSubscriberRequestPanelOpen(true);
    }
  }, [isSubscriberRecordLoading, isSubscriberRequestsListLoading, subscriberRequestsList, renewalReloadTriggered]);

  React.useEffect(() => {
    if (!isSubscriberRecordLoading && subscriberRecord) {
      const prodNearing = subscriberRecord.prodSubscriptionExpirationStatus === 1 || subscriberRecord.prodSubscriptionExpirationStatus === 2;
      const nonProdNearing = subscriberRecord.nonProdSubscriptionExpirationStatus === 1 || subscriberRecord.nonProdSubscriptionExpirationStatus === 2;
      setIsRenewEnabled(subscriberRecord.renewalEnabled && (prodNearing || nonProdNearing));
    }
  }, [isSubscriberRecordLoading, subscriberRecord]);

  const hasPendingRequests = (): boolean => {
    if (!subscriberRequestsList || subscriberRequestsList.length === 0) return false;
    return subscriberRequestsList.some(
      (request: SubscriberRequestRecord) => PENDING_STATUSES.includes(request.status)
    );
  };

  const hasActiveRenewalRequest = (environment: number): boolean => {
    if (!subscriberRequestsList || subscriberRequestsList.length === 0) return false;
    return subscriberRequestsList.some(
      (req) =>
        req.requestType?.toLowerCase() === 'renewal' &&
        req.environment === environment &&
        !req.isStale &&
        req.status !== 'Stale' &&
        req.status !== 'Rejected' &&
        (req.status === 'Draft' || req.status === 'Submitted' || req.status === 'PendingDataOwner' || req.status === 'PendingPrivacy' || req.status === 'PendingApproval')
    );
  };

  const handleRenewalRequest = async () => {
    if (hasPendingRequests()) {
      setShowPendingBlockDialog(true);
      return;
    }
    setShowRenewalPopup(true);
    setShowRenewalSuccess(false);
    setShowRenewalError(false);

    try {
      const promises: Promise<any>[] = [];

      const nonProdNearing = subscriberRecord.nonProdSubscriptionExpirationStatus === 1 || subscriberRecord.nonProdSubscriptionExpirationStatus === 2;
      if (nonProdNearing && !hasActiveRenewalRequest(0)) {
        promises.push(createRenewalRequest(subscriberRecord.id, 0));
      }

      const prodNearing = subscriberRecord.prodSubscriptionExpirationStatus === 1 || subscriberRecord.prodSubscriptionExpirationStatus === 2;
      if (prodNearing && !hasActiveRenewalRequest(1)) {
        promises.push(createRenewalRequest(subscriberRecord.id, 1));
      }

      if (promises.length === 0) {
        setShowRenewalPopup(false);
        return;
      }

      await Promise.all(promises);
      setShowRenewalSuccess(true);
    } catch {
      setShowRenewalError(true);
    }
  };
  
  const closeRenewalPopup = () => {
    setShowRenewalPopup(false);    
    setShowRenewalError(false);
     if (showRenewalSuccess) {
      setRenewalReloadTriggered(true);
    }
  };

  const onColumnClickSort = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
    let isSortedDescending = column.isSortedDescending ?? false;

    // If we've sorted this column, flip it.
    if (column.isSorted) {
      isSortedDescending = !isSortedDescending;
    }

    setSortedSubscriberRequestsList(prevItems => {
      const itemsCopy = [...prevItems];
      sortItems(itemsCopy, column.key!, isSortedDescending);
      return itemsCopy;
    });
    
    var newSubscriberRequestsTableColumns = subscriberRequestsTableColumns.map(col => {
      col.isSorted = col.key === column.key;
      if (col.isSorted) {
        col.isSortedDescending = isSortedDescending;
      }
      return col;
    });
    
    setSubscriberRequestsTableColumns(newSubscriberRequestsTableColumns);
  };

  const onHandleFilterColumn = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    if (item) {
      setFilterByColumn(item.key.toString());
      onHandleFilterValue(null,filterByValue);
    }
  }

  const onHandleFilterValue = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string) => {
    setFilterByValue(newValue);
    setSortedSubscriberRequestsList(prevItems => {
      const itemsCopy = [...subscriberRequestsList];
      if(newValue == '')
        return itemsCopy;

      newValue = newValue.toLowerCase();
      console.log("newValue" + newValue + " replaced: "+newValue.replace(' ',''));
      //Filter by Column data
      if(filterByColumn == "name")
        return itemsCopy.filter(i => i.name.toLowerCase().includes(newValue));
      else if(filterByColumn == "id")
        return itemsCopy.filter(i => i.id.toLowerCase().includes(newValue));
      else if (filterByColumn == "status"){
        return itemsCopy.filter(i => i.status.toLowerCase().includes(newValue.replace(' ','')));
      }
      else if (filterByColumn == "submittedDate")
        return itemsCopy.filter(i => i.submittedDate.includes(newValue));
      else if (filterByColumn == "environment"){
            return itemsCopy.filter(i => (i.environment == 1)? "production".includes(newValue): "nonproduction".includes(newValue.replace('-','')));
        }
      else if (filterByColumn == "subscriptionRequestType"){
            return itemsCopy.filter(i => {
              const isRenewal =i.requestType?.toLowerCase() === 'renewal';
              return (isRenewal ? "renewal" : "standard").includes(newValue);
            });
        }
      else if (filterByColumn == "egressOptions"){
            return itemsCopy.filter(i => getEgressOptionsDisplayString(i.egressOptions || []).toLowerCase().includes(newValue));
        }  
      else if (filterByColumn == '') {
          return itemsCopy.filter(i => (i.submittedDate.includes(newValue) || 
                                    i.status.toLowerCase().includes(newValue.replace(' ','')) || 
                                    i.id.toLowerCase().includes(newValue) || 
                                    i.name.toLowerCase().includes(newValue) ||
                                    getEgressOptionsDisplayString(i.egressOptions || []).toLowerCase().includes(newValue) ||
                                    (i.environment == 1)? "production".includes(newValue): "nonproduction".includes(newValue.replace('-','')))
                                  );
      }
      return itemsCopy;
    });
  }

  // Route Param: paramRequestId
  React.useEffect(() => {
    if (!isSubscriberRecordLoading && !isSubscriberRequestsListLoading && paramRequestId) {
      const paramSubscriberRequest = subscriberRequestsList.find((subscriberRequest) => {
        return subscriberRequest.id === paramRequestId
      });
      setSelectedViewRequest(paramSubscriberRequest);
      setViewSubscriberRequestPanelOpen(true);
    }
  }, [isSubscriberRecordLoading, isSubscriberRequestsListLoading]);
  
  const [subscriberRequestsTableColumns, setSubscriberRequestsTableColumns] = React.useState<IColumn[]>([
    {
      key: 'name',
      name: 'Name',
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
      onColumnClick: onColumnClickSort
    },
    {
      key: 'subscriptionRequestType',
      name: 'Request Type',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
      onColumnClick: onColumnClickSort
    },
    {
      key: 'submittedDate',
      name: 'Modified Date',
      minWidth: 80,
      maxWidth: 120,
      isResizable: true,
      sortDescendingAriaLabel: "Sorted in Descending order",
      isSortedDescending: true,
      isSorted: true,
      onColumnClick: onColumnClickSort
    },
    {
      key: 'environment',
      name: 'Environment',
      minWidth: 100,
      maxWidth: 120,
      isResizable: true,
      onColumnClick: onColumnClickSort
    },
    {
      key: 'egressOptions',
      name: 'Egress Option',
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
      onColumnClick: onColumnClickSort
    },
    {
      key: 'id',
      name: 'Request ID',
      minWidth: 270,
      maxWidth: 350,
      isResizable: true,
      onColumnClick: onColumnClickSort
    },
    {
      key: 'status',
      name: 'Status',
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: onColumnClickSort
    },
    {
      key: 'additionalNotes',
      name: 'Additional Notes',
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
      onColumnClick: onColumnClickSort
  },
  ]);

  const filterColumnDropdown: IDropdownOption[] = [{ key: '', text: ''}].concat(subscriberRequestsTableColumns.map((col, index) => {
    return { key: col.key, text: col.name};
  }));

  const commandBarItems: ICommandBarItemProps[] = [
    {
      key: 'renewRequest',
      text: 'Renew Subscription',
      iconProps: { iconName: 'Refresh' },
      disabled: !isRenewEnabled || isRenewalLoading || isSubscriberRecordLoading || isMyPermissionsLoading ||
                isSubscriberRequestsListLoading ||
                (hasActiveRenewalRequest(0) && hasActiveRenewalRequest(1)) ||
                (!(subscriberRecord?.nonProdSubscriptionExpirationStatus === 1 || subscriberRecord?.nonProdSubscriptionExpirationStatus === 2) || hasActiveRenewalRequest(0)) &&
                (!(subscriberRecord?.prodSubscriptionExpirationStatus === 1 || subscriberRecord?.prodSubscriptionExpirationStatus === 2) || hasActiveRenewalRequest(1)) ||
                (!myPermissions.isAdmin &&
                  (myPermissions.isPrivacyReviewer ||
                  myPermissions.isAuditor)),
      onClick: () => { 
        handleRenewalRequest(); 
      },
      buttonStyles: { 
        root: { 
          margin: '0px 0px 0px 0px',
          display: subscriberRecord?.renewalEnabled ? 'inline-block' : 'none'
        } 
      },
    },
    {
      key: 'addRequest',
      text: 'Add Request',
      iconProps: { iconName: 'Add' },
      disabled: (subscriberRecord?.renewalEnabled &&
        (subscriberRecord?.nonProdSubscriptionExpirationStatus === 1 || subscriberRecord?.nonProdSubscriptionExpirationStatus === 2) &&
        (subscriberRecord?.prodSubscriptionExpirationStatus === 1 || subscriberRecord?.prodSubscriptionExpirationStatus === 2)
      ) ||
      (isSubscriberRecordLoading && isMyPermissionsLoading) ||  
                  (!myPermissions.isAdmin && 
                   // To-Do: Implement => myPermissions.isConsumer
                  ( // myPermissions.isDataOwnerReviewer || 
                  myPermissions.isPrivacyReviewer || 
                  myPermissions.isAuditor)),
      onClick: () => {
        setAddSubscriberRequestPanelOpen(true);
      },
      buttonStyles: {
        root: {
          margin: '0px 0px 0px 0px',
        },
      },
    },
    {
      key: 'removeRequest',
      text: 'Remove Request',
      iconProps: { iconName: 'Delete' },
      disabled: removeRequestButtonDisabled,
      onClick: () => {
        handleRemoveRequestButton();
      },
      buttonStyles: {
        root: {
          margin: '0px 0px 0px 16px',
        },
      },
    },
    {
      key: 'attributeSubscriptions',
      text: 'Attribute Subscriptions',
      iconProps: { iconName: 'BulletedList' },
      disabled: isSubscriberRecordLoading,
      onClick: () => {
        setViewAllAttributeSubscriptionPanelOpen(true);
      },
      buttonStyles: {
        root: {
          margin: '0px 0px 0px 16px',
        },
      },
    },
    commandBarRefreshItem
  ];

  const handleRemoveRequestButton = () => {
    setShowConfirmationPopup(true);
    httpClient
      .delete(`${__API_BASE_URL__}/subscriber/deleteRequest?requestId=${selectedGridData.id}`, {
        resource: __CLIENT_ID__,
        headers: {
          'Content-Type': 'application/json',
          'X-Content-Type-Options': 'nosniff',
        },
      })
      .then(() => {
        setShowSuccessMessage(true);
      })
      .catch(() => {
        setShowErrorMessage(true);
      });
  };

  const onRenderItemColumn = (item: SubscriberRequestRecord, index: number, column: IColumn): React.ReactNode => {
    if (column.key === 'submittedDate') {
      const submittedDate = new Date(item.submittedDate);
      return <Text>{submittedDate.toLocaleDateString()}</Text>;
    } else if (column.key === 'environment' && item.environment == 0) {
      return <Text>Non-Production</Text>;
    } else if (column.key === 'environment' && item.environment == 1) {
      return <Text>Production</Text>;
    } else if (column.key === 'egressOptions') {
      const egressOptionsDisplay = getEgressOptionsDisplayString(item.receivedEgressOptions || []);
      return <Text>{egressOptionsDisplay}</Text>;
    } else if (column.key === 'subscriptionRequestType') {
      const isRenewal = item.requestType?.toLowerCase() === 'renewal';
      return <Text>{isRenewal ? 'Renewal' : 'Standard'}</Text>;
    } else if (column.key === 'status' && (item.status === 'Stale' || item.isStale)) {      
      return <Styled.StaleStatus>Stale</Styled.StaleStatus>;                                     
    } else if (column.key === 'status' && item.status === 'Draft') {
      return <Styled.DraftStatus>Draft</Styled.DraftStatus>;
    } else if (column.key === 'status' && item.status === 'Submitted') {
      return <Styled.SubmittedStatus>Submitted</Styled.SubmittedStatus>;
    } else if (column.key === 'status' && item.status === 'PendingDataOwner') {
      return <Styled.SubmittedStatus>Pending Data Owner</Styled.SubmittedStatus>;
    } else if (column.key === 'status' && item.status === 'PendingPrivacy') {
      return <Styled.SubmittedStatus>Pending Privacy</Styled.SubmittedStatus>;
    } else if (column.key === 'status' && item.status === 'PendingApproval') {
      return <Styled.SubmittedStatus>Pending Approval</Styled.SubmittedStatus>;
    } else if (column.key === 'status' && item.status === 'Rejected') {
      return <Styled.RejectedStatus>Rejected</Styled.RejectedStatus>;
    } else if (column.key === 'status' && item.status === 'Approved') {
      return <Styled.ApprovedStatus>Approved</Styled.ApprovedStatus>;
    } else if (column.key === 'status' && item.status === 'Provisioned') {
      return <Styled.ProvisionedStatus>Provisioned</Styled.ProvisionedStatus>;
    } else if (column.key === 'name' && (item.status === 'Stale' || item.isStale)) {           
      return <Styled.ConsumerName onClick={() => {                                                 
         onHandleViewRequest(item);                                                          
      }}>{item.name}</Styled.ConsumerName>;
    } else if (column.key === 'name' && item.status === 'Draft') {
      return <Styled.ConsumerName onClick={() => onHandleOpenDraft(item)}>{item.name}</Styled.ConsumerName>;
    } else if (column.key === 'name' && item.status === 'Rejected') {
      return <Styled.ConsumerName onClick={() => onHandleOpenDraft(item)}>{item.name}</Styled.ConsumerName>;
    } else if (column.key === 'name') {
      return <Styled.ConsumerName onClick={() => onHandleViewRequest(item)}>{item.name}</Styled.ConsumerName>;
    } else if (column.key === 'id') {
      return <Text>{item.id}</Text>;
    }
    if (column.key === 'additionalNotes') {
      const noteText = item.additionalNotes || '';
      if (item.status !== 'Rejected' || !noteText) return null;
      const truncated = TRUNCATED_NOTES(noteText);
      if (noteText.length > MAX_NOTES_LENGTH) {
        return (
         <TooltipHost styles={{root: { cursor: 'pointer' }}} content={noteText}>
          <span>{truncated}</span>
         </TooltipHost>
        );
      }
      return <span>{noteText}</span>;
    }
  };

  const onRenderRow = (props: IDetailsRowProps) => {
    return <DetailsRow aria-busy={true} {...props} />;
  };

  const onRenderCheckbox = (props: IDetailsListCheckboxProps | undefined) => {
    return <CoherenceCheck {...props} />;
  };

  const onHandleOpenDraft = (item: SubscriberRequestRecord) => {
    setSelectedEditedRequest(item);
    setEditedSubscriberRequestPanelOpen(true);
  };

  const onHandleViewRequest = (item: SubscriberRequestRecord) => {
    const sanitizedRequest: SubscriberRequestRecord = {
      ...item,
      name: item.name || 'N/A',
      description: item.description || 'N/A',
      id: item.id || 'N/A',
      environment: item.environment || 0,
      egressOptions: item.egressOptions || [],
      serviceBusNamespace: item.serviceBusNamespace || 'N/A',
      serviceBusTopic: item.serviceBusTopic || 'N/A',
      effectiveDatedCDM: item.effectiveDatedCDM || 'N/A',
      privacyApprovalArtifacts: item.privacyApprovalArtifacts || [],
      privacyApprovalDate: item.privacyApprovalDate || new Date().toISOString(),
      additionalNotes: item.additionalNotes || 'N/A',
      privacyTeamName: item.privacyTeamName || 'N/A',
    };
    setSelectedViewRequest(sanitizedRequest);
    setViewSubscriberRequestPanelOpen(true);
  };

  const selection = new Selection({
    onSelectionChanged: () => {
      changeSubscriberTableSelection();
    },
  });

  const changeSubscriberTableSelection = () => {
    const selectionCount = selection.getSelectedCount();
    if (selectionCount == 0) {
      setRemoveRequestButtonDisabled(true);
    } else {
      const selectedSubscriberRequestRecord: SubscriberRequestRecord =
        selection.getSelection()[0] as SubscriberRequestRecord;
      setSelectedGridData(selectedSubscriberRequestRecord);
      if (selectedSubscriberRequestRecord.status === 'Draft' && !selectedSubscriberRequestRecord.isStale) {
        setRemoveRequestButtonDisabled(false);
      } else if (selectedSubscriberRequestRecord.status === 'Rejected' && !selectedSubscriberRequestRecord.isStale) {
        setRemoveRequestButtonDisabled(false);
      } else {
        setRemoveRequestButtonDisabled(true);
      }
    }
  };

  const handlePivotChange = (item?: PivotItem) => {
    if (item) {
      setSelectedPivotKey(item.props.itemKey);
    }
  };

  const dismissAddSubscriberRequestPanel = () => {
    setAddSubscriberRequestPanelOpen(false);
  };

  const dismissEditedSubscriberRequestPanel = () => {
    setSelectedEditedRequest(null);
    setEditedSubscriberRequestPanelOpen(false);
  };

  const dismissViewSubscriberRequestPanel = () => {
    setSelectedViewRequest(null);
    setViewSubscriberRequestPanelOpen(false);
  };

  const dismissViewApprovedAttributesPanel = () => {
    setViewAllAttributeSubscriptionPanelOpen(false);
  };

  const closeConfirmationPopup = () => {
    setShowConfirmationPopup(false);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
  };

  const filterSubscriberRequestsList = () => {
    console.log("loading filterSubscriberRequestsList");
    if (!sortedSubscriberRequestsList) {
      return [];
    } else if (selectedPivotKey != 'Requests') {
      const newFilteredSortedSubscriberRequestsList = sortedSubscriberRequestsList.filter(
        (subscriberRequest: SubscriberRequestRecord) => {
          return subscriberRequest.status == selectedPivotKey;
        }
      );
      return newFilteredSortedSubscriberRequestsList;
    }
    return sortedSubscriberRequestsList;
  };

  const isPrivacy = !isSubscriberRecordLoading && !isMyPermissionsLoading && myPermissions.isPrivacyReviewer && !myPermissions.isAdmin;
  if (isPrivacy && selectedPivotKey != 'PendingPrivacy') setSelectedPivotKey('PendingPrivacy');

  const isDataOwner = !isSubscriberRecordLoading && !isMyPermissionsLoading && myPermissions.isDataOwnerReviewer && !myPermissions.isAdmin && !isPrivacy;
  
  const isOtherUser = !isSubscriberRecordLoading && !isMyPermissionsLoading && !(isPrivacy);  //|| isDataOwner); 
  
  // To-Do: Should add logic to check for isConsumer permissions

  return (
    <div data-testid="subscriber-management-page">
      {isSubscriberRecordLoading ? (
        <SubscriberDetailsHeaderLoading />
      ) : subscriberError ? (
        <SubscriberDetailsHeaderError message={subscriberError}/>
      ) : subscriberRecord ? (
        <SubscriberDetailsHeader subscriberRecord={subscriberRecord} isAdmin={myPermissions.isAdmin} />
      ) : null}

    {!isSubscriberRecordLoading && subscriberRecord && subscriberRecord.renewalEnabled &&
      (subscriberRecord.prodSubscriptionExpirationStatus === 1 || subscriberRecord.nonProdSubscriptionExpirationStatus === 1) && (
        <MessageBar messageBarType={MessageBarType.warning} isMultiline={false} styles={{ root: { margin: '30px 30px' } }}>
          Your subscription is nearing expiration. Please renew your subscription.
        </MessageBar>
    )}
    {!isSubscriberRecordLoading && subscriberRecord && subscriberRecord.renewalEnabled &&
      (subscriberRecord.prodSubscriptionExpirationStatus === 2 || subscriberRecord.nonProdSubscriptionExpirationStatus === 2) && (
        <MessageBar messageBarType={MessageBarType.severeWarning} isMultiline={false} styles={{ root: { margin: '30px 30px' } }}>
          Your subscription has expired. Please renew immediately.
        </MessageBar>
      )}

      {(!isSubscriberRequestsListLoading && isPrivacy) ? (
        <Styled.PortalManagementPivot
          aria-label="Subscriber Request Tables Pivot"
          selectedKey="PendingPrivacy"
          onLinkClick={handlePivotChange}
          defaultSelectedKey="PendingPrivacy"
        >
          <PivotItem
            headerText="Requests (Pending Privacy Review)"
            itemKey="PendingPrivacy"
            headerButtonProps={{
              'data-order': 1,
              'data-title': 'PendingPrivacy',
            }}
          />
        </Styled.PortalManagementPivot>
      ) :
      // To-Do: Enhance security by extending isConsumer access instead of isOtherUser
      (!isSubscriberRequestsListLoading && isDataOwner && isOtherUser) ? (
        (<Styled.PortalManagementPivot
          aria-label="Subscriber Request Tables Pivot"
          selectedKey={selectedPivotKey}
          onLinkClick={handlePivotChange}
          defaultSelectedKey="PendingDataOwner"
        >
          <PivotItem
            headerText="Requests (Pending Data Owner Review)"
            itemKey="PendingDataOwner"
            headerButtonProps={{
              'data-order': 1,
              'data-title': 'PendingDataOwner',
            }}
          />
          <PivotItem
            headerText="Subscription Requests"
            itemKey="Requests"
            headerButtonProps={{
              'data-order': 2,
              'data-title': 'Requests',
            }}
          />
        </Styled.PortalManagementPivot>
      )) :
      (!isSubscriberRequestsListLoading && isDataOwner) ? (
        <Styled.PortalManagementPivot
          aria-label="Subscriber Request Tables Pivot"
          selectedKey="PendingDataOwner"
          onLinkClick={handlePivotChange}
          defaultSelectedKey="PendingDataOwner"
        >
          <PivotItem
            headerText="Requests (Pending Data Owner Review)"
            itemKey="PendingDataOwner"
            headerButtonProps={{
              'data-order': 1,
              'data-title': 'PendingDataOwner',
            }}
          />
        </Styled.PortalManagementPivot>
      ) :
      (!isSubscriberRequestsListLoading && isOtherUser) ? (
        <Styled.PortalManagementPivot
          aria-label="Subscriber Request Tables Pivot"
          selectedKey={selectedPivotKey}
          onLinkClick={handlePivotChange}
          defaultSelectedKey="Requests"
        >
          <PivotItem
            headerText="Subscription Requests"
            itemKey="Requests"
            headerButtonProps={{
              'data-order': 1,
              'data-title': 'Requests',
            }}
          />
        </Styled.PortalManagementPivot>
      ) : (<></>)}

      <Styled.ButtonsTableSection>
        <Styled.HorizontalScrollWrapper>
          <Styled.DataOwnerTableContentWrapper>
            <Styled.PortalManagementFilterDiv>
              <CommandBar items={commandBarItems} ariaLabel="Command Bar" />

              <FontIcon aria-label="filter" iconName="Filter">
              </FontIcon>
              <label>Filter by Column</label>
              <Styled.DropdownStyled
                data-testid="select-column"
                placeholder="Filter by column"
                ariaLabel="Column"
                onChange={onHandleFilterColumn}
                selectedKey={filterByColumn}
                options={filterColumnDropdown}
                multiSelect={false}
              />
              <label>Filter by Value</label>
              <TextField
                onChange={onHandleFilterValue}
                placeholder="Enter text"
                value={filterByValue}
              />
            </Styled.PortalManagementFilterDiv>
            <MarqueeSelection selection={selection}>
              {renderRequestTable && (
                <>
                  <ShimmeredDetailsList
                    items={filterSubscriberRequestsList()}
                    enableShimmer={isSubscriberRequestsListLoading}
                    columns={subscriberRequestsTableColumns}
                    onRenderItemColumn={onRenderItemColumn}
                    onRenderRow={onRenderRow}
                    onRenderCheckbox={(props) => onRenderCheckbox(props)}
                    selection={selection}
                    selectionMode={SelectionMode.single}
                    layoutMode={DetailsListLayoutMode.fixedColumns}
                    constrainMode={ConstrainMode.unconstrained}
                    checkboxVisibility={CheckboxVisibility.always}
                    ariaLabelForShimmer="Subscribers Table is being fetched"
                    ariaLabelForGrid="Subscribers Table"
                    checkButtonAriaLabel="Select Subscriber Request"
                  />
                </>
              )}
            </MarqueeSelection>
          </Styled.DataOwnerTableContentWrapper>
        </Styled.HorizontalScrollWrapper>
      </Styled.ButtonsTableSection>

      {addSubscriberRequestPanelOpen && (
        <AddSubscriberRequestPanel
          isOpen={addSubscriberRequestPanelOpen}
          dismissPanel={dismissAddSubscriberRequestPanel}
          setShowConfirmationPopup={setShowConfirmationPopup}
          setShowSuccessMessage={setShowSuccessMessage}
          setShowErrorMessage={setShowErrorMessage}
          subscriberId={subscriberRecord.id}
          subscriberRecord={subscriberRecord}
        />
      )}

      {editedSubscriberRequestPanelOpen && (
        <AddSubscriberRequestPanel
          isOpen={editedSubscriberRequestPanelOpen}
          dismissPanel={dismissEditedSubscriberRequestPanel}
          setShowConfirmationPopup={setShowConfirmationPopup}
          setShowSuccessMessage={setShowSuccessMessage}
          setShowErrorMessage={setShowErrorMessage}
          subscriberId={subscriberRecord.id}
          subscriberRequestDefault={selectedEditedRequest}
          subscriberRecord={subscriberRecord}
        />
      )}

      {viewSubscriberRequestPanelOpen && (
        <ViewSubscriberRequestPanel
          isOpen={viewSubscriberRequestPanelOpen}
          dismissPanel={dismissViewSubscriberRequestPanel}
          subscriberRequest={selectedViewRequest}
        />
      )}

      {viewAllAttributeSubscriptionPanelOpen && (
        <ViewApprovedAttributesPanel
          isOpen={viewAllAttributeSubscriptionPanelOpen}
          dismissPanel={dismissViewApprovedAttributesPanel}
          subscriberId={subscriberRecord.id}
        />
      )}

      <Dialog
        hidden={!showConfirmationPopup}
        onDismiss={closeConfirmationPopup}
        dialogContentProps={{
          type: DialogType.normal,
        }}
        modalProps={{
          isBlocking: showSuccessMessage || showErrorMessage ? false : true,
          styles: { main: { maxWidth: 450 } },
        }}
      >
        <Stack>
          <Label styles={{ root: { fontSize: 16 } }}>
            {!showSuccessMessage && !showErrorMessage && PopupText.requestInProgress}
            {showSuccessMessage && PopupText.requestSuccess}
            {showErrorMessage && PopupText.userError}
          </Label>
          {!showSuccessMessage && !showErrorMessage && <ProgressIndicator></ProgressIndicator>}
          <Text styles={{ root: { fontSize: 12, paddingTop: 15 } }}>
            {!showSuccessMessage && !showErrorMessage && ''}
            {showSuccessMessage && PopupText.requestSuccessDescription}
            {showErrorMessage && PopupText.userErrorDescription}
          </Text>
        </Stack>
      </Dialog>
      <Dialog
        hidden={!showRenewalPopup}
        onDismiss={closeRenewalPopup}
        dialogContentProps={{ type: DialogType.normal }}
        modalProps={{
          isBlocking: showRenewalSuccess || showRenewalError ? false : true,
          styles: { main: { maxWidth: 450 } },
        }}
      >
        <Stack>
          <Label styles={{ root: { fontSize: 16 } }}>
            {!showRenewalSuccess && !showRenewalError && renewalInProgress}
            {showRenewalSuccess && renewalSuccess}
            {showRenewalError && renewalError}
          </Label>
          {!showRenewalSuccess && !showRenewalError && <ProgressIndicator />}
          <Text styles={{ root: { fontSize: 12, paddingTop: 15 } }}>
            {!showRenewalSuccess && !showRenewalError && ''}
            {showRenewalSuccess && renewalSuccessDescription}
            {showRenewalError && renewalErrorDescription}
          </Text>
        </Stack>
      </Dialog>
      <Dialog
        hidden={!showPendingBlockDialog}
        onDismiss={() => setShowPendingBlockDialog(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Renewal Not Allowed',
          subText: 'A renewal request cannot be submitted while there are active requests (Pending Data Owner Review, Pending Privacy Review, or Pending Approval). Please wait until the active requests have been processed.',
        }}
        modalProps={{ isBlocking: false, styles: { main: { maxWidth: 500 } } }}
      >
        <DialogFooter>
          <DefaultButton onClick={() => setShowPendingBlockDialog(false)} text="OK" />
        </DialogFooter>
      </Dialog>
    </div>
  );
}
